import { ActionIcon, Card, Group, MultiSelect, Stack, TextInput, Title } from "@mantine/core";
import { useIntl, FormattedMessage } from "react-intl";
import { useMemo, type FC } from "react";
import type { Layer, Source } from "../NewTileset";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { TippecanoeOptionsInput } from "./TippecanoeOptionsInput";
import { CommandPreview } from "./CommandPreview";
import { buildOgr2ogrCommand, buildTippecanoeCommand, convertedName, needsConversion } from "../utils/buildCommand";
import { cleanOptions } from "../constants/tippecanoeFields";

export interface SourcesInputProps {
  isLoading: boolean;
  layers: Layer[];
  sources: Source[];
  onAddLayer: () => void;
  getInputProps: (name: string, opts?: { type?: "checkbox" | "input" }) => object;
  onRemoveLayer: (index: number) => void;
}

interface LayerCommandsProps {
  layer: Layer;
  sources: Source[];
}

/** Build the ogr2ogr → tippecanoe command chain for a single layer preview. */
function LayerCommands({ layer, sources }: LayerCommandsProps) {
  const commands = useMemo(() => {
    const layerName = layer.name || "<layer>";
    const sourceById = new Map(sources.map(s => [s.id, s]));
    const sourceFiles = layer.sources.map((id) => {
      const s = sourceById.get(id);
      return s?.file?.name ?? s?.name ?? "<source>";
    });

    const entries: string[] = [];
    const tippecanoeInputs: string[] = [];
    for (const fileName of sourceFiles) {
      if (needsConversion(fileName)) {
        const converted = convertedName(fileName);
        entries.push(buildOgr2ogrCommand(fileName, converted));
        tippecanoeInputs.push(converted);
      }
      else {
        tippecanoeInputs.push(fileName);
      }
    }

    entries.push(buildTippecanoeCommand(
      tippecanoeInputs,
      `${layerName}.mbtiles`,
      { ...cleanOptions(layer.options), layer: layerName },
    ));
    return entries;
  }, [layer, sources]);

  return <CommandPreview commands={commands} />;
}

export const LayersInput: FC<SourcesInputProps> = ({
  isLoading,
  layers,
  sources,
  getInputProps, onRemoveLayer, onAddLayer }) => {
  const intl = useIntl();

  const nameLabel = intl.formatMessage({
    defaultMessage: "Name",
    id: "label.name",
  });
  const sourcesLabel = intl.formatMessage({
    defaultMessage: "Sources",
    id: "label.sources",
  });

  const layerDescriptionLabel = intl.formatMessage({
    defaultMessage: "Layers must have a source",
    id: "label.tilesetLayersDescription",
  });

  const last = layers.length - 1;

  return (
    <Stack gap="xs">
      <Title order={3}>
        <FormattedMessage defaultMessage="Layers" id="label.tilesetLayers" />
      </Title>
      {layerDescriptionLabel}

      {layers.map((layer, index) => (
        <Card key={index} withBorder shadow="sm" radius="md">
          <Stack gap="xs">
            <Group gap="xs" align="flex-start">
              <TextInput
                flex={1}
                label={nameLabel}
                disabled={isLoading}
                placeholder={nameLabel}
                {...getInputProps(`layers.${index}.name`)}
              />
              <MultiSelect
                flex={1}
                data={sources.map(s => ({ value: s.id, label: s.name }))}
                label={sourcesLabel}
                disabled={isLoading}
                placeholder={sourcesLabel}
                clearable
                {...getInputProps(`layers.${index}.sources`)}
              />

              <Group my="auto" gap="xs">
                {last !== 0 && (
                  <ActionIcon color="red.8" onClick={() => { onRemoveLayer(index); }}>
                    <IconTrash size="1rem" />
                  </ActionIcon>
                )}

                {index === last && (
                  <ActionIcon color="green.8" onClick={() => { onAddLayer(); }}>
                    <IconPlus size="1rem" />
                  </ActionIcon>
                )}
              </Group>
            </Group>

            <TippecanoeOptionsInput
              basePath={`layers.${index}.options`}
              isLoading={isLoading}
              getInputProps={getInputProps}
            />

            <LayerCommands layer={layer} sources={sources} />
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};
